import {
    MediaPackProgram,
    MediaProgram,
    WorkflowWorkflowInfo,
    WorkflowWorkflowState,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { routes } from '../routes';

export enum ProgramState {
    NotJoined = 'not-joined',
    InProgress = 'in-progress',
    Completed = 'completed',
    Abandoned = 'abandoned',
    Unspecified = '',
}

export const workflowStateToProgramStateMap = {
    [WorkflowWorkflowState.Created]: ProgramState.InProgress,
    [WorkflowWorkflowState.InProgress]: ProgramState.InProgress,
    [WorkflowWorkflowState.Completed]: ProgramState.Completed,
    [WorkflowWorkflowState.Abandoned]: ProgramState.Abandoned,
};

export function getClinicalProgramState(workflow?: WorkflowWorkflowInfo) {
    if (!workflow) {
        return ProgramState.Unspecified;
    }

    if (!workflow?.workflowInstance) {
        return ProgramState.NotJoined;
    }

    return (
        workflowStateToProgramStateMap[
            workflow?.workflowInstance?.state ?? ''
        ] ?? ProgramState.Unspecified
    );
}

export function getProgramType(
    program?: MediaProgram,
    workflow?: WorkflowWorkflowInfo
) {
    return workflow?.workflowDefn.type ?? program?.isClinical
        ? WorkflowWorkflowType.ClinicalProgram
        : undefined;
}

export function getProgramSessionDurationRange(program: MediaProgram) {
    return program.guides?.reduce(
        ({ min, max }, guide) => ({
            min: Math.min(min, guide?.totalDuration ?? Infinity),
            max: Math.max(max, guide?.totalDuration ?? 0),
        }),
        { min: Infinity, max: 0 }
    );
}

/** will move completed programs to the back if willSortCompletion is true */
export const sortPrograms =
    (willSortCompletion = true) =>
    (a?: MediaProgram, b?: MediaProgram) => {
        const completionSort = willSortCompletion
            ? (a?.completed ? 1 : 0) - (b?.completed ? 1 : 0)
            : 0;
        return completionSort
            ? completionSort
            : a?.title?.localeCompare(b?.title ?? '') ?? 0;
    };

export function getIsJoined(workflow?: WorkflowWorkflowInfo) {
    return getClinicalProgramState(workflow) === ProgramState.InProgress;
}

/**
 * Retrieves the `pack_class` metadata from the given program.
 *
 * @param {MediaProgram | MediaPackProgram} [program] - The program object, which may contain metadata.
 * @returns {string | undefined} The `pack_class` value if it exists and is a string; otherwise, `undefined`.
 */
export function getProgramPackClassFromMetadata(
    program?: MediaProgram | MediaPackProgram
): string | undefined {
    const packClass = program?.metadata?.pack_class;
    return typeof packClass === 'string' ? packClass : undefined;
}

/**
 * Creates the program details route for the provided program.
 * Prefers the pack_class in the program metadata, when present, for efficient retrieval of associated workflows and recommendations
 *
 * @param {MediaProgram} [program] - The program object, which may contain metadata.
 * @param {packClass} [string] - The pack class that the program was sent in. Used as a fallback if the program has no metadata.
 * @param {showOverview} [string] - If present, forces the program page to show the overview tab, not the schedule tab
 * @returns {string | undefined} The route (for use in e.g. href or router.push) for the program detail page
 */
export function getProgramRoute(
    program: MediaProgram,
    packClass: string,
    showOverview?: string
): string;
export function getProgramRoute(
    program?: MediaProgram,
    packClass?: string,
    showOverview?: string
): string | undefined;
export function getProgramRoute(
    program?: MediaProgram,
    packClass?: string,
    showOverview = ''
): string | undefined {
    if (!program) {
        return undefined;
    }
    const packClassForPush =
        getProgramPackClassFromMetadata(program) ?? packClass;
    if (!packClassForPush) {
        return undefined;
    }
    return routes.programDetails(packClassForPush, program.id, showOverview);
}
